import { useTranslations } from '@/i18n/translations';
import type {
  CommentErrorKind,
  CommentItemView,
  CommentLeaderRemoval,
} from '../comments/index';
import {
  useDeleteFeedComment,
  useReactToFeedComment,
  useRemoveFeedComment,
  useUpdateFeedComment,
} from '../../lib/mutations/feed';
import type { PostRemovalTarget } from './post-removal-targets';
import { EDITOR_BODY_FORMAT } from '../editor';
import { useAuthedSession } from '../../lib/use-session';
import { commentPath } from '../../lib/routes';
import { feedErrorMessage } from './feed-error-copy';
import { useErrorMessage } from '../../lib/error-message';
import type { ReactionKind } from '@leanin/shared/reactions';

// Per-comment actions for a post's comments — edit, delete, react, copy link,
// report, leader removal — shared by the full thread (PostComments) and the feed card's
// comment preview, so both render the same CommentItem with the same menu.
export function usePostCommentHandlers({
  postId,
  canReact,
  removalGroup,
}: {
  postId: string;
  canReact: boolean;
  /** The group a leader removes this post's comments from (see PostCard's
   *  removal targets); undefined when the viewer leads none of its groups. */
  removalGroup: PostRemovalTarget | undefined;
}) {
  const t = useTranslations('Feed');
  const describeError = useErrorMessage();
  const session = useAuthedSession();
  const updateComment = useUpdateFeedComment();
  const deleteComment = useDeleteFeedComment();
  const reactToComment = useReactToFeedComment();
  const removeComment = useRemoveFeedComment();
  const leaderRemoval: CommentLeaderRemoval | undefined =
    removalGroup === undefined
      ? undefined
      : {
          groupKind: removalGroup.groupKind,
          onRemove: async (comment, input) => {
            await removeComment.mutateAsync({
              commentId: comment.id,
              postId,
              groupId: removalGroup.groupId,
              // A removed top-level comment takes its replies with it.
              threadSize: 1 + (comment.replyCount ?? 0),
              ...input,
            });
          },
        };

  return {
    viewerUserId: session?.userId ?? null,
    onUpdate: async (commentId: string, body: string) => {
      const updated = await updateComment.mutateAsync({
        commentId,
        postId,
        body,
        bodyFormat: EDITOR_BODY_FORMAT,
      });
      return {
        body: updated.body,
        bodyFormat: updated.bodyFormat,
        mentions: updated.mentions,
      };
    },
    onDelete: async (comment: CommentItemView) => {
      // Deleting a top-level comment deletes its thread server-side; size the
      // cached commentCount patch accordingly (a reply has no replies).
      const threadSize = 1 + (comment.replyCount ?? 0);
      await deleteComment.mutateAsync({ commentId: comment.id, postId, threadSize });
    },
    formatError: (err: unknown, kind: CommentErrorKind) =>
      feedErrorMessage(err, describeError, t, `comments.errors.${kind}`),
    reportKind: 'comment' as const,
    leaderRemoval,
    commentHref: (commentId: string) => commentPath(postId, commentId),
    ...(canReact
      ? {
          onReact: async (commentId: string, kind: ReactionKind) => {
            const result = await reactToComment.mutateAsync({
              commentId,
              postId,
              kind,
            });
            return {
              reactionCounts: result.reactionCounts,
              viewerReaction: result.viewerReaction,
            };
          },
        }
      : {}),
  };
}
